<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useEventListener } from '@vueuse/core';
import { useAToast } from '@Atoms';
import { getDefaultDateFormat } from '@Helpers';
import { useSessionStore } from '@Stores';
import { ResendStaffEmailVerificationService, SendStaffEmailVerificationService } from '@StaffModule';

const emits = defineEmits<{
  onRefreshData: [];
}>();

const props = defineProps<{
  field: string;
  rowData: any;
}>();

const { shelterProfile, userProfile } = storeToRefs(useSessionStore());

const { notify, TOAST_TYPES } = useAToast();

const popoverId = `email-popover-${Math.random().toString(36).slice(2)}`;

const isPopoverOpen = ref(false);
const loading = ref(false);
const panel = ref();
const triggerRef = useTemplateRef<any>('triggerRef');

const sentAt = computed(() => props.rowData?.email_verification_sent_at || null);
const verifiedAt = computed(() => props.rowData?.email_verified_at || null);
const verificationStatus = computed(() => props.rowData?.email_verification_status || 'pending');

const iconClass = computed(() => {
  // prettier-ignore
  return verificationStatus.value === 'verified' ? 'pi pi-check-circle text-green-600'
       : verificationStatus.value === 'sent' ? 'pi pi-send text-sky-600'
       : 'pi pi-clock text-amber-500';
});

const resendButtonLabel = computed(() =>
  loading.value
    ? 'shelterInfo.staffProfile.emailVerification.actions.resending'
    : 'shelterInfo.staffProfile.emailVerification.actions.resend'
);

const statusLabel = computed(() => {
  // prettier-ignore
  return verificationStatus.value === 'verified' ? 'shelterInfo.staffProfile.emailVerification.status.verified'
       : verificationStatus.value === 'sent' ? 'shelterInfo.staffProfile.emailVerification.status.sent'
       : 'shelterInfo.staffProfile.emailVerification.status.pending';
});

const showEmailVerificationUi = computed(
  () => !!shelterProfile.value?.shelter_two_factor_enabled && userProfile.value?.role === 'admin'
);

useEventListener(document, 'click', onDocumentClick);
useEventListener(window, 'af-staff-email-popover-opened', onStaffEmailPopoverOpened);

onBeforeUnmount(onComponentBeforeUnmount);

// HELPERS
function asDate(dateValue: any) {
  if (!dateValue) return '-';

  const formatted = getDefaultDateFormat(dateValue, true);

  return formatted || '-';
}

function getTriggerElement(): HTMLElement | null {
  const triggerElement = triggerRef.value;
  if (!triggerElement) return null;

  if (typeof triggerElement.contains === 'function') {
    return triggerElement as HTMLElement;
  }

  const rootElement = triggerElement?.$el;
  if (rootElement && typeof rootElement.contains === 'function') {
    return rootElement as HTMLElement;
  }

  return null;
}

// EVENTS
function onToggleDetails(event: Event) {
  window.dispatchEvent(
    new CustomEvent('af-staff-email-popover-opened', {
      detail: { popoverId },
    })
  );

  panel.value?.toggle(event);
}

function onSendVerification() {
  if (loading.value) return;

  loading.value = true;

  SendStaffEmailVerificationService(props.rowData._id)
    .then(() => {
      const TOAST_SUCCESS = {
        title: 'shelterInfo.staffProfile.emailVerification.toast.sent',
        type: TOAST_TYPES.SUCCESS,
      };
      notify(TOAST_SUCCESS);

      emits('onRefreshData');

      panel.value?.hide();
    })
    .catch((error: any) => {
      const TOAST_ERROR = {
        title: error?.response?.data?.error || 'errors.generics.unknownIssue.title',
        type: TOAST_TYPES.ERROR,
      };
      notify(TOAST_ERROR);
    })
    .finally(() => (loading.value = false));
}

function onResendVerification() {
  if (loading.value) return;

  loading.value = true;

  ResendStaffEmailVerificationService(props.rowData._id)
    .then(() => {
      const TOAST_SUCCESS = {
        title: 'shelterInfo.staffProfile.emailVerification.toast.resent',
        type: TOAST_TYPES.SUCCESS,
      };
      notify(TOAST_SUCCESS);

      emits('onRefreshData');

      panel.value?.hide();
    })
    .catch((error: any) => {
      const TOAST_ERROR = {
        title: error?.response?.data?.error || 'errors.generics.unknownIssue.title',
        type: TOAST_TYPES.ERROR,
      };
      notify(TOAST_ERROR);
    })
    .finally(() => (loading.value = false));
}

function onPopoverShow() {
  isPopoverOpen.value = true;
}

function onDocumentClick(event: MouseEvent) {
  if (!isPopoverOpen.value) return;

  const target = event.target as Node | null;
  const triggerEl = getTriggerElement();
  const panelEl = panel.value?.container as HTMLElement | undefined;

  const clickedTrigger = !!(target && triggerEl && triggerEl.contains(target));
  const clickedPanel = !!(target && panelEl?.contains(target));

  if (!clickedTrigger && !clickedPanel) {
    panel.value?.hide();
  }
}

function onPopoverHide() {
  isPopoverOpen.value = false;
}

function onStaffEmailPopoverOpened(event: Event) {
  const customEvent = event as CustomEvent<{ popoverId?: string }>;
  const openedPopoverId = customEvent?.detail?.popoverId;

  if (!openedPopoverId || openedPopoverId === popoverId) return;

  if (isPopoverOpen.value) {
    panel.value?.hide();
  }
}

function onComponentBeforeUnmount() {
  panel.value?.hide();
}
</script>

<template>
  <div class="flex items-center gap-2">
    <Button
      v-if="showEmailVerificationUi"
      text
      rounded
      class="h-6 w-6"
      ref="triggerRef"
      :loading
      :icon="iconClass"
      @click.stop="onToggleDetails"
    />

    <span class="truncate">
      {{ field || '-' }}
    </span>

    <OverlayPanel
      v-if="showEmailVerificationUi"
      ref="panel"
      @hide="onPopoverHide"
      @show="onPopoverShow"
    >
      <div class="min-w-64 space-y-2 text-sm">
        <p class="font-semibold">{{ $t(statusLabel) }}</p>

        <p class="max-w-[260px] text-xs text-gray-500">
          {{ $t('shelterInfo.staffProfile.emailVerification.description') }}
        </p>

        <p v-if="verificationStatus === 'sent'">
          {{ $t('shelterInfo.staffProfile.emailVerification.lastSent') }}: {{ asDate(sentAt) }}
        </p>

        <p
          v-if="verificationStatus === 'verified'"
          class="text-sm text-gray-500"
        >
          {{ $t('shelterInfo.staffProfile.emailVerification.verifiedAt') }}: {{ asDate(verifiedAt) }}
        </p>

        <Button
          v-if="verificationStatus === 'pending'"
          class="af-button af-button-success w-fit text-sm"
          :loading
          :disabled="loading"
          :label="$t('shelterInfo.staffProfile.emailVerification.actions.send')"
          @click="onSendVerification"
        />

        <Button
          v-if="verificationStatus === 'sent'"
          class="af-button af-button-success w-fit text-sm"
          :loading
          :disabled="loading"
          :label="$t(resendButtonLabel)"
          @click="onResendVerification"
        />
      </div>
    </OverlayPanel>
  </div>
</template>
